import React, { useCallback, useState } from "react";
import { useOkapiKy } from "@folio/stripes/core";
import { Button, Checkbox, ConfirmationModal, ErrorModal, TextField, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchRegistrationEmailStatus, RegistrationEmailRunningError, startRegistrationEmail, stopRegistrationEmail, } from "../../api/adminService";
import styles from "./RegistrationEmail.module.css";
var STATUS_QUERY_KEY = ["admin", "registration-email-status"];
var POLL_INTERVAL_MS = 3000;
var DATE_FORMAT = { dateStyle: "short", timeStyle: "medium" };
var DEFAULT_DELAY_MS = 1000;
var MAX_DELAY_MS = 60000;
/**
 * The delay typed by the admin as whole milliseconds within the range the backend accepts, or null when invalid.
 */
var parseDelay = function (value) {
    if (!/^\d+$/.test(value.trim())) {
        return null;
    }
    var delay = Number(value);
    return delay <= MAX_DELAY_MS ? delay : null;
};
/**
 * Admin section that sends the initialization (complete-registration) email to all users: shows how
 * many emails will be sent and how many users already received it, lets the admin set the delay
 * between two emails, asks for confirmation (optionally resending to those already sent), reports
 * the status of the last run and stops a running one.
 */
export var RegistrationEmail = function () {
    var _a, _b, _c, _d;
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _e = useIntl(), formatMessage = _e.formatMessage, formatDate = _e.formatDate;
    var _f = useState(false), confirmOpen = _f[0], setConfirmOpen = _f[1];
    var _g = useState(false), resendAll = _g[0], setResendAll = _g[1];
    var _h = useState(String(DEFAULT_DELAY_MS)), delayInput = _h[0], setDelayInput = _h[1];
    var _j = useState(null), errorMessage = _j[0], setErrorMessage = _j[1];
    var showError = useCallback(function (error) {
        console.error("Registration email error:", error);
        setErrorMessage(error instanceof RegistrationEmailRunningError
            ? formatMessage({ id: "ui-br-users.admin.registration.email.error.running" })
            : formatMessage({ id: "ui-br-users.admin.registration.email.error" }, { reason: error.message }));
    }, [formatMessage]);
    var status = useQuery(STATUS_QUERY_KEY, function () { return fetchRegistrationEmailStatus(ky); }, {
        refetchInterval: function (current) { return ((current === null || current === void 0 ? void 0 : current.state) === "RUNNING" ? POLL_INTERVAL_MS : false); },
        onError: showError,
    }).data;
    var onRunChanged = {
        onSuccess: function (changed) {
            queryClient.setQueryData(STATUS_QUERY_KEY, changed);
        },
        onError: function (error) {
            showError(error);
            queryClient.invalidateQueries(STATUS_QUERY_KEY);
        },
    };
    var _k = useMutation(function (request) { return startRegistrationEmail(ky, request); }, onRunChanged), start = _k.mutate, isStarting = _k.isLoading;
    var _l = useMutation(function () { return stopRegistrationEmail(ky); }, onRunChanged), stop = _l.mutate, isStopping = _l.isLoading;
    var isRunning = (status === null || status === void 0 ? void 0 : status.state) === "RUNNING";
    var isStopRequested = isStopping || !!(status === null || status === void 0 ? void 0 : status.stopRequested);
    var recipientCount = (_a = status === null || status === void 0 ? void 0 : status.recipientCount) !== null && _a !== void 0 ? _a : 0;
    var alreadySentCount = (_b = status === null || status === void 0 ? void 0 : status.alreadySentCount) !== null && _b !== void 0 ? _b : 0;
    var pendingCount = Math.max(recipientCount - alreadySentCount, 0);
    var delay = parseDelay(delayInput);
    var confirmCount = resendAll ? recipientCount : pendingCount;
    var openConfirm = useCallback(function () {
        setResendAll(false);
        setConfirmOpen(true);
    }, []);
    var closeConfirm = useCallback(function () { return setConfirmOpen(false); }, []);
    var closeError = useCallback(function () { return setErrorMessage(null); }, []);
    var confirmStart = useCallback(function () {
        setConfirmOpen(false);
        if (delay !== null && confirmCount > 0) {
            start({ resendAll: resendAll, delayMs: delay });
        }
    }, [start, resendAll, delay, confirmCount]);
    var formatTime = function (value) { return (value ? formatDate(value, DATE_FORMAT) : ""); };
    return (React.createElement(React.Fragment, null,
        React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.title", tagName: "h1" }),
        React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.description", tagName: "p" }),
        status && (React.createElement(React.Fragment, null,
            React.createElement("p", null,
                React.createElement("strong", null,
                    React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.recipients", values: { count: pendingCount } }))),
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.alreadySent", values: { count: alreadySentCount } })))),
        status && status.state !== "IDLE" && (React.createElement("p", null,
            React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.state.".concat(status.state), values: {
                    startedAt: formatTime(status.startedAt),
                    finishedAt: formatTime(status.finishedAt),
                    sent: status.sent,
                    failed: status.failed,
                    total: status.total,
                    planned: (_c = status.plannedCount) !== null && _c !== void 0 ? _c : 0,
                    error: (_d = status.error) !== null && _d !== void 0 ? _d : "",
                } }))),
        React.createElement(ConfirmationModal, { open: confirmOpen, bodyTag: "div", isConfirmButtonDisabled: confirmCount === 0, heading: React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.confirm.heading" }), message: React.createElement(React.Fragment, null,
                React.createElement("p", null, confirmCount === 0 ? (React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.confirm.nothing" })) : (React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.confirm.message", values: { count: confirmCount } }))),
                React.createElement("p", null,
                    React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.confirm.delay", values: { delay: delay !== null && delay !== void 0 ? delay : 0 } })),
                React.createElement(Checkbox, { label: formatMessage({ id: "ui-br-users.admin.registration.email.confirm.resendAll" }), checked: resendAll, onChange: function (event) { return setResendAll(event.target.checked); } })), onConfirm: confirmStart, onCancel: closeConfirm, confirmLabel: React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.confirm.confirm" }), cancelLabel: React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.confirm.cancel" }), buttonStyle: "primary" }),
        React.createElement(ErrorModal, { label: React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.title" }), open: errorMessage !== null, content: errorMessage !== null && errorMessage !== void 0 ? errorMessage : "", onClose: closeError }),
        React.createElement("div", { className: styles.controls },
            React.createElement("div", { className: styles.delay },
                React.createElement(TextField, { type: "number", min: 0, max: MAX_DELAY_MS, step: 1, label: formatMessage({ id: "ui-br-users.admin.registration.email.delay.label" }), value: delayInput, onChange: function (event) { return setDelayInput(event.target.value); }, error: delay === null
                        ? formatMessage({ id: "ui-br-users.admin.registration.email.delay.invalid" })
                        : undefined, disabled: isRunning })),
            React.createElement("div", { className: styles.buttons },
                React.createElement(Button, { buttonStyle: "primary", onClick: openConfirm, disabled: !status || isStarting || isRunning || recipientCount === 0 || delay === null }, isStarting || isRunning ? (React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.button.loading" })) : (React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.button" }))),
                isRunning && (React.createElement(Button, { onClick: function () { return stop(); }, disabled: isStopRequested }, isStopRequested ? (React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.stop.button.loading" })) : (React.createElement(FormattedMessage, { id: "ui-br-users.admin.registration.email.stop.button" }))))))));
};
